import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers, Paintbrush, LayoutGrid, Building2, Home, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../data/translations';
import { serviceImages } from '../data/references';

export const ServicesOverview = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const services = [
    { id: 'gipserarbeiten', icon: Layers, title: t.services.gipserarbeiten.title, description: t.services.gipserarbeiten.short, image: serviceImages.gipserarbeiten, color: '#E31C23' },
    { id: 'malerarbeiten', icon: Paintbrush, title: t.services.malerarbeiten.title, description: t.services.malerarbeiten.short, image: serviceImages.malerarbeiten, color: '#00A651' },
    { id: 'trockenbau', icon: LayoutGrid, title: t.services.trockenbau.title, description: t.services.trockenbau.short, image: serviceImages.trockenbau, color: '#C41230' },
    { id: 'aussenfassaden', icon: Building2, title: t.services.aussenfassaden.title, description: t.services.aussenfassaden.short, image: serviceImages.aussenfassaden, color: '#E31C23' },
    { id: 'innenausbau', icon: Home, title: t.services.innenausbau.title, description: t.services.innenausbau.short, image: serviceImages.innenausbau, color: '#00A651' }
  ];

  return (
    <section className="py-20 md:py-28 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4" data-testid="services-title">{t.services.title}</h2>
          <p className="text-lg text-gray-600 max-w-2xl">{t.services.subtitle}</p>
          <div className="w-20 h-1 bg-[#E31C23] mt-6" />
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div key={service.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }} className={`group relative overflow-hidden bg-white border border-gray-100 hover:shadow-2xl transition-all duration-500 ${index === 0 ? 'lg:col-span-2' : ''}`} data-testid={`service-card-${service.id}`}>
              <div className="relative h-64 overflow-hidden">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute top-4 left-4 w-12 h-12 flex items-center justify-center" style={{ backgroundColor: service.color }}><service.icon className="w-6 h-6 text-white" /></div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-bold text-[#1A1A1A] mb-2 group-hover:text-[#E31C23] transition-colors">{service.title}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{service.description}</p>
                <Link to={`/dienstleistungen/${service.id}`} className="inline-flex items-center space-x-2 text-[#E31C23] font-medium text-sm"><span>{t.services.learnMore}</span><ArrowRight className="w-4 h-4" /></Link>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mt-12">
          <Link to="/dienstleistungen" className="inline-flex items-center space-x-2 bg-[#1A1A1A] text-white px-8 py-4 font-semibold hover:bg-[#333] transition-colors" data-testid="services-view-all"><span>{t.services.allServices}</span><ArrowRight className="w-5 h-5" /></Link>
        </motion.div>
      </div>
    </section>
  );
};
